import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, BookOpen, Clock, MapPin, PackageSearch, ShieldCheck, Store as StoreIcon, Truck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import StoreShell from "@/components/StoreShell";
import { hexA } from "@/lib/storeutils";
import { footerLines, POLICY_LINKS, freeShipNote } from "@/lib/storenav";
import api from "@/lib/api";
import { PageTitle } from "./Shop";
import { Splash, ErrorView } from "./Storefront";

function Page({ title, subtitle, testId, children }) {
  const s = useStore();
  if (s.loading) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;
  return (
    <StoreShell>
      <PageTitle s={s} title={title} subtitle={subtitle} />
      <section className="max-w-6xl mx-auto px-5 py-10" data-testid={testId}>{children(s)}</section>
    </StoreShell>
  );
}

const Card = ({ s, children, className = "" }) => (
  <div className={`rounded-2xl p-6 ${className}`} style={{ background: s.c.surface, border: `1px solid ${s.border}`, boxShadow: s.cardShadow }}>{children}</div>
);

const Cta = ({ s, to, children, solid = true }) => (
  <Link to={to} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-transform hover:scale-[1.03]"
    style={solid ? { background: s.c.accent, color: "#fff" } : { border: `1px solid ${s.border}`, color: s.c.text }}>{children}</Link>
);

export function About() {
  return (
    <Page title="About us" subtitle={null} testId="about-page">
      {(s) => (
        <div className="grid md:grid-cols-3 gap-6">
          <Card s={s} className="md:col-span-2">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: hexA(s.c.primary, 0.12), color: s.c.primary }}><StoreIcon size={22} /></span>
              <div><h2 className="text-xl font-extrabold" style={{ fontFamily: s.font }}>{s.store.name}</h2>{s.store.business_name && <div className="text-sm" style={{ color: s.muted }}>{s.store.business_name}</div>}</div>
            </div>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: s.muted }}>{s.store.tagline || `Welcome to ${s.store.name}.`}</p>
            <p className="mt-3 text-[15px] leading-relaxed" style={{ color: s.muted }}>
              We are an online {s.store.business_category ? s.store.business_category.toLowerCase() : ""} store. Browse the catalogue, order in a few clicks and track your delivery any time{s.categories.length ? ` — ${s.categories.length} categor${s.categories.length === 1 ? "y" : "ies"} and ${s.products.length} product${s.products.length === 1 ? "" : "s"} to explore` : ""}.
            </p>
            <div className="mt-6 flex flex-wrap gap-3"><Cta s={s} to={`/store/${s.slug}/shop`}>Shop now <ArrowRight size={15} /></Cta><Cta s={s} to={`/store/${s.slug}/contact`} solid={false}>Contact us</Cta></div>
          </Card>
          <div className="space-y-4">
            {[[BadgeCheck, "Quality you can trust", "Products are checked before they ship."], [Truck, "Fast delivery", freeShipNote(s.store) ? `${freeShipNote(s.store)}.` : "Tracked, courier-delivered orders."], [ShieldCheck, "Secure checkout", "Pay online safely or choose cash on delivery."]].map(([I, h, d]) => (
              <Card key={h} s={s} className="!p-4 flex items-start gap-3"><I size={20} style={{ color: s.c.accent }} className="mt-0.5 shrink-0" /><div><div className="font-bold text-sm">{h}</div><div className="text-xs mt-0.5" style={{ color: s.muted }}>{d}</div></div></Card>
            ))}
          </div>
        </div>
      )}
    </Page>
  );
}

export function Contact() {
  return (
    <Page title="Contact us" subtitle="We're here to help with your order." testId="contact-page">
      {(s) => {
        const lines = footerLines(s.store);
        return (
          <div className="grid md:grid-cols-2 gap-6">
            <Card s={s}>
              <h2 className="text-lg font-extrabold" style={{ fontFamily: s.font }}>{s.store.name}</h2>
              <ul className="mt-4 space-y-3 text-sm" style={{ color: s.muted }}>
                {lines.location && <li className="flex gap-3"><MapPin size={17} className="mt-0.5 shrink-0" style={{ color: s.c.accent }} /><span><b style={{ color: s.c.text }}>Business</b><br />{lines.location}</span></li>}
                <li className="flex gap-3"><Clock size={17} className="mt-0.5 shrink-0" style={{ color: s.c.accent }} /><span><b style={{ color: s.c.text }}>Hours</b><br />Online 24×7 — place an order any time</span></li>
                <li className="flex gap-3"><PackageSearch size={17} className="mt-0.5 shrink-0" style={{ color: s.c.accent }} /><span><b style={{ color: s.c.text }}>Order support</b><br />{lines.support}</span></li>
              </ul>
            </Card>
            <Card s={s} className="flex flex-col justify-center">
              <h2 className="text-lg font-extrabold" style={{ fontFamily: s.font }}>Need help with an order?</h2>
              <p className="mt-2 text-sm" style={{ color: s.muted }}>Enter your order number and email on the tracking page to see the live status of your delivery.</p>
              <div className="mt-5 flex flex-wrap gap-3"><Cta s={s} to={`/store/${s.slug}/track`}>Track my order <ArrowRight size={15} /></Cta><Cta s={s} to={`/store/${s.slug}/shop`} solid={false}>Continue shopping</Cta></div>
            </Card>
          </div>
        );
      }}
    </Page>
  );
}

const postDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

function Cover({ s, src, className = "" }) {
  return src
    ? <img src={src} alt="" loading="lazy" className={`w-full object-cover ${className}`} />
    : <div className={`w-full flex items-center justify-center ${className}`} style={{ background: hexA(s.c.primary, 0.08), color: s.c.primary }}><BookOpen size={30} /></div>;
}

function BlogList({ s }) {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(`/store/${s.slug}/blogs`).then(({ data }) => live && setPosts(data)).catch(() => live && setPosts([]));
    return () => { live = false; };
  }, [s.slug]);

  if (posts === null) return <div className="py-16 text-center text-sm" style={{ color: s.muted }}>Loading posts…</div>;
  if (!posts.length) {
    return (
      <Card s={s} className="text-center py-14">
        <span className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: hexA(s.c.primary, 0.1), color: s.c.primary }}><BookOpen size={26} /></span>
        <h2 className="mt-4 text-lg font-extrabold" style={{ fontFamily: s.font }}>No blog posts yet</h2>
        <p className="mt-1 text-sm" style={{ color: s.muted }}>We haven&apos;t published any posts. Check back soon.</p>
        <div className="mt-5"><Cta s={s} to={`/store/${s.slug}/shop`}>Browse the shop <ArrowRight size={15} /></Cta></div>
      </Card>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
      {posts.map((p) => (
        <Link key={p.id} to={`/store/${s.slug}/blogs/${p.slug}`} data-testid={`blog-card-${p.slug}`}
          className="group rounded-2xl overflow-hidden flex flex-col transition-transform hover:-translate-y-1" style={{ background: s.c.surface, border: `1px solid ${s.border}`, boxShadow: s.cardShadow }}>
          <div className="overflow-hidden"><Cover s={s} src={p.cover} className="aspect-[16/10] transition-transform duration-500 group-hover:scale-105" /></div>
          <div className="p-5 flex flex-col flex-1">
            <div className="text-xs" style={{ color: s.muted }}>{[postDate(p.published_at), p.author].filter(Boolean).join(" · ")}</div>
            <h2 className="mt-1.5 text-lg font-extrabold leading-snug" style={{ fontFamily: s.font }}>{p.title}</h2>
            {p.excerpt && <p className="mt-2 text-sm line-clamp-3" style={{ color: s.muted }}>{p.excerpt}</p>}
            <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: s.c.accent }}>Read more <ArrowRight size={14} /></span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function Blogs() {
  return (
    <Page title="Blogs" subtitle="News, tips and stories from the store." testId="blogs-page">
      {(s) => <BlogList s={s} />}
    </Page>
  );
}

function PostBody({ s, slug }) {
  const [state, setState] = useState({ loading: true, post: null });
  useEffect(() => {
    let live = true;
    setState({ loading: true, post: null });
    api.get(`/store/${s.slug}/blogs/${slug}`).then(({ data }) => live && setState({ loading: false, post: data }))
      .catch(() => live && setState({ loading: false, post: null }));
    return () => { live = false; };
  }, [s.slug, slug]);

  if (state.loading) return <div className="py-16 text-center text-sm" style={{ color: s.muted }}>Loading…</div>;
  const post = state.post;
  if (!post) {
    return (
      <Card s={s} className="text-center py-14">
        <h2 className="text-lg font-extrabold" style={{ fontFamily: s.font }}>Post not found</h2>
        <p className="mt-1 text-sm" style={{ color: s.muted }}>This post may have been removed.</p>
        <div className="mt-5"><Cta s={s} to={`/store/${s.slug}/blogs`}>All posts <ArrowRight size={15} /></Cta></div>
      </Card>
    );
  }
  const paragraphs = (post.body || "").split(/\n\s*\n/).map((t) => t.trim()).filter(Boolean);
  return (
    <article className="max-w-3xl mx-auto" data-testid="blog-post">
      <Link to={`/store/${s.slug}/blogs`} className="text-sm font-semibold" style={{ color: s.c.accent }}>← All posts</Link>
      <h1 className="mt-3 text-3xl md:text-4xl font-extrabold leading-tight" style={{ fontFamily: s.font }} data-testid="blog-post-title">{post.title}</h1>
      <div className="mt-2 text-sm" style={{ color: s.muted }}>{[postDate(post.published_at), post.author && `By ${post.author}`].filter(Boolean).join(" · ")}</div>
      {post.cover && <img src={post.cover} alt="" className="mt-6 w-full rounded-2xl object-cover max-h-[460px]" />}
      <div className="mt-6 space-y-4 text-base leading-relaxed">
        {paragraphs.map((t, i) => <p key={i} style={{ whiteSpace: "pre-line" }}>{t}</p>)}
      </div>
    </article>
  );
}

export function BlogPost() {
  const { postSlug } = useParams();
  return (
    <Page title="Blogs" subtitle="News, tips and stories from the store." testId="blog-post-page">
      {(s) => <PostBody s={s} slug={postSlug} />}
    </Page>
  );
}

/* Policy pages (privacy, terms, returns, IP) — plain text written by the vendor in Setup → Policy Pages. */
function PolicyBody({ s, pageKey, title }) {
  const [state, setState] = useState({ loading: true, content: "" });
  useEffect(() => {
    let live = true;
    setState({ loading: true, content: "" });
    api.get(`/store/${s.slug}/policy/${pageKey}`).then(({ data }) => live && setState({ loading: false, content: data.content || "" }))
      .catch(() => live && setState({ loading: false, content: "" }));
    return () => { live = false; };
  }, [s.slug, pageKey]);
  if (state.loading) return <div className="text-sm" style={{ color: s.muted }}>Loading…</div>;
  if (!state.content) return <Card s={s}><p className="text-[15px]" style={{ color: s.muted }}>{title} hasn&apos;t been published yet. Please contact the store for details.</p></Card>;
  return <Card s={s}><div className="text-[15px] leading-relaxed whitespace-pre-line break-words" style={{ color: s.muted }} data-testid="policy-content">{state.content}</div></Card>;
}

export function PolicyPage({ pageKey }) {
  const title = (POLICY_LINKS.find(([k]) => k === pageKey) || [])[1] || "Policy";
  return (
    <Page title={title} subtitle={null} testId={`policy-page-${pageKey}`}>
      {(s) => <PolicyBody s={s} pageKey={pageKey} title={title} />}
    </Page>
  );
}
